import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Calendar,
    Download,
    ChevronRight,
    FileText,
    Share2,
    Twitter,
    Facebook,
    Linkedin,
    Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { getInsightBySlug, getAllPublishedInsights } from '@/services/insight';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from "@/components/ui/skeleton";
import { encodeS3Url } from "@/components/encodeS3Url";
import { capitalizeWords, toSentenceCase } from '@/helper';
import SEO from '@/components/seo/seo';

interface RecentInsightCardProps {
    insight: any;
}

const RecentInsightCard = ({ insight }: RecentInsightCardProps) => {
    return (
        <Link to={`/market-insights/${insight.slug}`} className="group block">
            <div className="flex gap-4 items-start">
                <div className="relative flex-shrink-0 w-24 h-24 overflow-hidden bg-muted">
                    <img
                        src={insight.featuredImage ? encodeS3Url(insight.featuredImage) : "/placeholder.svg"}
                        alt={insight.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                </div>
                <div className="flex-grow space-y-2">
                    <h4 className="text-sm font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
                        {capitalizeWords(insight.title)}
                    </h4>
                    {insight.description && (
                        <p className="text-[11px] text-muted-foreground line-clamp-1 leading-normal tracking-relaxed">
                            {insight.description}
                        </p>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span>
                            {new Date(insight.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="opacity-40">|</span>
                        <span className="font-semibold text-primary/90 uppercase tracking-tight text-[10px]">
                            {insight.category}
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const MarketInsightDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const [insight, setInsight] = useState<any>(null);
    const [recentInsights, setRecentInsights] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchInsightAndRecent = async () => {
            if (!slug) return;
            try {
                // Fetch current insight
                const currentInsight = await getInsightBySlug(slug);
                setInsight(currentInsight);

                // Fetch recent insights
                const response = await getAllPublishedInsights({ page: 1, limit: 5 });
                if (response && response.data && Array.isArray(response.data)) {
                    // Filter out current insight
                    const filtered = response.data.filter((i: any) => i.slug !== slug);
                    setRecentInsights(filtered);
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInsightAndRecent();
    }, [slug]);

    if (isLoading) {
        return (
            <div className="min-h-screen pt-24 pb-16">
                <div className="container mx-auto px-4 py-6">
                    <Skeleton className="h-4 w-64 mb-6" />
                </div>
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Left Column: Content */}
                        <div className="lg:col-span-8 space-y-10">
                            <div className="space-y-4">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-10 md:h-14 w-full" />
                                <Skeleton className="h-[1px] w-full" />

                                <div className="flex items-center justify-between py-1">
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-8 w-24" />
                                </div>

                                <Skeleton className="h-[1px] w-full" />
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                </div>

                                <Skeleton className="h-[1px] w-full mt-8 mb-2" />
                                <div className="flex gap-3">
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                </div>
                            </div>

                            {/* Featured Image Skeleton */}
                            <Skeleton className="aspect-video lg:aspect-[21/9] w-full" />

                            {/* Content Skeleton */}
                            <div className="space-y-4">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                            </div>
                        </div>

                        {/* Sidebar Column */}
                        <div className="lg:col-span-4 space-y-10">
                            <div className="space-y-6">
                                <Skeleton className="h-8 w-48 border-b pb-4" />
                                <div className="space-y-6">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="flex gap-4">
                                            <Skeleton className="w-24 h-24 flex-shrink-0" />
                                            <div className="flex-grow space-y-2">
                                                <Skeleton className="h-4 w-full" />
                                                <Skeleton className="h-3 w-2/3" />
                                                <Skeleton className="h-3 w-1/2" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!insight) {
        return (
            <div className="pt-32 pb-20 flex-1 flex flex-col items-center justify-center text-center px-4">
                <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                <h1 className="text-2xl font-bold mb-2">Insight Not Found</h1>
                <p className="text-muted-foreground mb-6">The market insight you are looking for does not exist or has been removed.</p>
                <Link to="/market-insights">
                    <Button>Back to Market Insights</Button>
                </Link>
            </div>
        );
    }

    return (
        <>
            <SEO
                title={`${toSentenceCase(insight.title)} | Urban Oasis UAE`}
                description={insight.description}
                url={`/market-insights/${insight.slug}`}
                image={insight.featuredImage || "/image/logo.png"}
                keywords="dubai real estate, urban oasis, market insights"
                type="article"
            />
            <div className="pt-24 pb-16">
                {/* Breadcrumb */}
                <div className="container mx-auto px-4 py-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                        <ChevronRight className="h-4 w-4" />
                        <Link to="/market-insights" className="hover:text-primary transition-colors">Market Insights</Link>
                        <ChevronRight className="h-4 w-4" />
                        <span className="text-foreground font-medium truncate max-w-[200px]">{capitalizeWords(insight.title)}</span>
                    </div>
                </div>

                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Left Column */}
                        <div className="lg:col-span-8 space-y-10">
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="px-2.5 py-0.5 text-[11px] font-semibold bg-primary/5 text-primary border-primary/10 hover:bg-primary/10 transition-colors uppercase tracking-wider">
                                        {capitalizeWords(insight.category)}
                                    </Badge>
                                </div>
                                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-[1.2] tracking-tight">
                                    {capitalizeWords(insight.title)}
                                </h1>

                                <div className="w-full h-[1px] bg-gray-200" />

                                <div className="flex items-center justify-between py-1">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center text-sm text-[#555555]">
                                            <Calendar className="h-4 w-4 mr-2 opacity-70" />
                                            <span>
                                                {new Date(insight.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Integrated Download Option */}
                                    {/* {insight.pdfFile && ( */}
                                    <Button asChild size="sm" variant="outline" className="gap-2 border-primary/20 hover:bg-primary/5">
                                        <a href={encodeS3Url(insight.pdfFile)} target="_blank" rel="noopener noreferrer" download>
                                            <Download className="h-4 w-4" />
                                            Report
                                        </a>
                                    </Button>
                                    {/* )} */}
                                </div>

                                <div className="w-full h-[1px] bg-gray-200" />

                                {insight.description && (
                                    <p className="leading-relaxed border-primary/20">
                                        {toSentenceCase(insight.description)}
                                    </p>
                                )}

                                <div className="w-full h-[1px] bg-gray-200 mt-8 mb-2" />

                                <div className="flex items-center gap-3">
                                    <span className="text-[12px] font-medium text-[#999999] uppercase tracking-wider mr-1">Share</span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-black hover:text-white transition-all"
                                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${insight.title}&url=${window.location.href}`, '_blank')}
                                    >
                                        <Twitter className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-blue-600 hover:text-white transition-all"
                                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                                    >
                                        <Facebook className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-blue-700 hover:text-white transition-all"
                                        onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${insight.title}`, '_blank')}
                                    >
                                        <Linkedin className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-primary hover:text-white transition-all"
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.href);
                                            toast({
                                                title: "Link Copied",
                                                description: "Insight link copied to clipboard.",
                                            });
                                        }}
                                    >
                                        <Share2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>

                            {/* Featured Image */}
                            {insight.featuredImage && (
                                <div className="mb-10 overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[16/9] lg:aspect-[21/9] relative group">
                                    <img
                                        src={encodeS3Url(insight.featuredImage)}
                                        alt={capitalizeWords(insight.title)}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                            )}

                            <div className="prose dark:prose-invert max-w-none">
                                <div
                                    className="text-foreground/90 text-lg leading-relaxed [&_img]:aspect-[16/9] [&_img]:lg:aspect-[21/9] [&_img]:object-cover [&_img]:rounded-none [&_img]:my-10"
                                    dangerouslySetInnerHTML={{ __html: insight.contentHTML }}
                                />
                            </div>
                        </div>

                        {/* Sidebar Column */}
                        <div className="lg:col-span-4 lg:sticky lg:top-24 h-fit space-y-10">
                            {/* Recent Insights Section */}
                            {recentInsights.length > 0 && (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h3 className="text-xl font-bold text-foreground">Recent Insights</h3>
                                        <Link to="/market-insights" className="text-sm font-medium text-primary hover:underline">View All</Link>
                                    </div>
                                    <div className="grid gap-6">
                                        {recentInsights.map((recentInsight) => (
                                            <RecentInsightCard key={recentInsight._id} insight={recentInsight} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MarketInsightDetail;
